import React, { useEffect, useState, memo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { RootState } from '../redux/store'
import { WidgetLayout } from '../../lib/widget-layout'
import Text from '../../ui/Text'
import hasClaimForCompany from '../../lib/hasClaimForCompany'
import Claims from '../../lib/claims'
import { Button } from '../../common/components/Button'
import {
    useCompanyUpdateUserMutation,
    useCompanyUpdateRoleToUserMutation,
    useCompanyCreateUserMutation,
    useCompanyInviteExistingUserToCompanyMutation,
    useCompanyAddRoleToUserMutation,
    ChargePointUser,
} from '../../common/redux/api/web-api'
import canChangePassword from '../../lib/canChangePassword'
import { notifyError, notifySuccess } from '../../common/features/notificationsManager/Notify'
import { ButtonContainer } from '../../common/styles/Button'
import useText from '../../common/hooks/useText'

import { Form, Column } from './UserDetails.styling'
import UserDetailsPassword from './UserDetailsPassword'

const UserDetails = memo(function UserDetails(): React.ReactElement {
    const text = useText()
    const navigate = useNavigate()
    const signedInUser = useSelector((state: RootState) => state.user)
    const user = useSelector((state: RootState) => state.selectedUser)
    const [selectedUser, setSelectedUser] = useState<ChargePointUser>(user)
    const [openPasswordChange, setOpenPasswordChange] = useState<boolean>(false)

    const checkPassword = (password?: string | null, passwordCompare?: string): string | null => {
        if (password !== passwordCompare) {
            return text('msgPasswordDoNotMatch')
        }
        if (password && password.length < 8) {
            return text('msgPasswordToShort')
        }
        return null
    }

    const isAdmin = () => {
        return hasClaimForCompany(signedInUser, signedInUser.selectedCompanyId, Claims.Admin)
    }

    const showChangePassword = () => {
        return canChangePassword(signedInUser, selectedUser) || (isAdmin() && selectedUser.passwordChangeableForAdmin)
    }

    const closeChangePassword = () => {
        if (openPasswordChange) setSelectedUser({ ...selectedUser, passwordChange: null })
        setOpenPasswordChange(!openPasswordChange)
    }

    useEffect(() => {
        setSelectedUser(user)
    }, [user])

    const disableSave = () => {
        if (!user.id)
            return (
                !selectedUser.firstName ||
                !selectedUser.lastName ||
                !selectedUser.email ||
                !selectedUser.password ||
                !selectedUser.roleInCompany
            )

        if (openPasswordChange)
            return (
                !selectedUser.firstName ||
                !selectedUser.lastName ||
                !selectedUser.email ||
                !selectedUser.passwordChange?.newPassword
            )
        return (
            !selectedUser.firstName ||
            !selectedUser.lastName ||
            !selectedUser.email ||
            updatingUser ||
            updatingRole ||
            creatingUser ||
            invitingUser ||
            addingRole
        )
    }

    const [updateUser, { isLoading: updatingUser }] = useCompanyUpdateUserMutation()
    const [updateRole, { isLoading: updatingRole }] = useCompanyUpdateRoleToUserMutation()
    const [createUser, { isLoading: creatingUser }] = useCompanyCreateUserMutation()
    const [inviteUser, { isLoading: invitingUser }] = useCompanyInviteExistingUserToCompanyMutation()
    const [addRole, { isLoading: addingRole }] = useCompanyAddRoleToUserMutation()

    const save = async (e?: React.FormEvent<HTMLFormElement> | undefined) => {
        e?.preventDefault()
        if (selectedUser.id) {
            try {
                if (openPasswordChange) {
                    const { newPassword } = e?.currentTarget.elements as any
                    const error = checkPassword(selectedUser.passwordChange?.newPassword, newPassword?.value)

                    if (error) {
                        notifyError(error, false)
                        return
                    }
                }

                await updateUser({ companyId: signedInUser.selectedCompanyId, chargePointUser: selectedUser }).unwrap()
                const role = {
                    claim: selectedUser.roleInCompany,
                    companyId: signedInUser.selectedCompanyId,
                    chargePointUserId: selectedUser.id,
                }

                await updateRole({ companyId: signedInUser.selectedCompanyId, userId: selectedUser.id, role }).unwrap()
                notifySuccess(text(openPasswordChange ? 'msgProfilePasswordUpdated' : 'msgUserUpdated'), true)
            } catch (e) {
                notifyError(text('msgUserNotSaved'), true)
                console.error(e)
            }
        } else {
            const { passwordCompare1 } = e?.currentTarget.elements as any
            const error = checkPassword(selectedUser.password, passwordCompare1?.value)
            if (error) {
                notifyError(error, false)
                return
            }

            try {
                selectedUser.userStatus = 'Valid'
                const res: ChargePointUser = await createUser({
                    companyId: signedInUser.selectedCompanyId,
                    chargePointUser: selectedUser,
                }).unwrap()
                const role = {
                    claim: selectedUser.roleInCompany,
                    companyId: signedInUser.selectedCompanyId,
                    chargePointUserId: res.id,
                }

                await addRole({ companyId: signedInUser.selectedCompanyId, userId: res.id ?? '', role }).unwrap()
                navigate('/users')
                notifySuccess(text('lblNewUsercreated'), true)
            } catch (e: any) {
                if (e.status === 412) {
                    notifyError(text('msgUserAlreadyInOrg'), true)
                } else if (e.status === 409) {
                    if (confirm(text('msgShouldSendInviteToExistingUser'))) {
                        try {
                            await inviteUser({
                                companyId: signedInUser.selectedCompanyId,
                                chargePointUser: selectedUser,
                                role: selectedUser.roleInCompany ?? '',
                            }).unwrap()

                            notifySuccess(text('msgInviteSent'), true)
                            navigate('/users')
                        } catch (e) {
                            notifyError(text('msgInviteNotSent'), true)
                        }
                    }
                }
            }
        }
    }

    const isLoading = updatingRole || updatingUser || creatingUser || invitingUser || addingRole

    return (
        <WidgetLayout
            title={
                !user.id
                    ? text('lblNewUserPage')
                    : text('lblDetailsForPartnerAdmins', {
                          firstName: user.firstName,
                          lastName: user.lastName,
                      })
            }
            data-testid="userDetails"
        >
            <Form onSubmit={save} autoComplete="off" noValidate>
                <Column data-testid="firstName">
                    <Text code="lblFirstNameField" />
                    <input
                        className="form-control"
                        value={selectedUser.firstName ?? ''}
                        onChange={(e) => setSelectedUser({ ...selectedUser, firstName: e.target.value })}
                        disabled={!isAdmin()}
                        data-testid="firstName-input"
                    ></input>
                </Column>

                <Column data-testid="lastName">
                    <Text code="lblLastNameField" />
                    <input
                        className="form-control"
                        value={selectedUser.lastName ?? ''}
                        onChange={(e) => setSelectedUser({ ...selectedUser, lastName: e.target.value })}
                        disabled={!isAdmin()}
                        data-testid="lastName-input"
                    ></input>
                </Column>

                <Column data-testid="email">
                    <Text code="lblEmailField" />
                    <input
                        className="form-control"
                        type="email"
                        name="email"
                        autoComplete="email"
                        value={selectedUser.email ?? ''}
                        onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        disabled={user.id || !isAdmin()}
                        data-testid="email-input"
                    ></input>
                </Column>

                <Column data-testid="mobile">
                    <Text code="lblMobileField" />
                    <input
                        className="form-control"
                        type="tel"
                        value={selectedUser.mobile ?? ''}
                        onChange={(e) => setSelectedUser({ ...selectedUser, mobile: e.target.value })}
                        disabled={!isAdmin()}
                        data-testid="mobile-input"
                    ></input>
                </Column>

                {!user.id && (
                    <Column data-testid="password">
                        <Text code="lblNewPasswordField" />
                        <input
                            className="form-control"
                            type="password"
                            value={selectedUser.password ?? ''}
                            onChange={(e) => setSelectedUser({ ...selectedUser, password: e.target.value })}
                            disabled={!isAdmin()}
                            data-testid="password-input"
                        ></input>
                    </Column>
                )}

                {!user.id && (
                    <Column data-testid="password1">
                        <Text code="lblConfirmPasswordField" />
                        <input
                            className="form-control"
                            type="password"
                            id="passwordCompare1"
                            disabled={!isAdmin()}
                            data-testid="password1-input"
                        ></input>
                    </Column>
                )}

                <div>
                    <div className="align-center" data-testid="user">
                        <input
                            type="radio"
                            name="role"
                            id="role1"
                            value="User"
                            disabled={!isAdmin()}
                            data-testid="radio-user"
                            onChange={(e) => setSelectedUser({ ...selectedUser, roleInCompany: e.target.value })}
                            checked={selectedUser.roleInCompany === 'User'}
                        />
                        <span className="margin-top-6" data-testid="lbl-radio-userView">
                            <Text code="lblUserTypeUser" />
                        </span>
                    </div>
                    <div className="align-center" data-testid="admin">
                        <input
                            type="radio"
                            name="role"
                            id="role2"
                            value="Admin"
                            disabled={!isAdmin()}
                            checked={selectedUser.roleInCompany === 'Admin'}
                            onChange={(e) => setSelectedUser({ ...selectedUser, roleInCompany: e.target.value })}
                            data-testid="radio-admin"
                        />
                        <span className="margin-top-6" data-testid="lbl-radio-admin">
                            <Text code="lblUserTypeAdmin" />
                        </span>
                    </div>
                    <div className="align-center" data-testid="adminViewOnly">
                        <input
                            type="radio"
                            name="role"
                            id="role3"
                            value="AdminViewOnly"
                            disabled={!isAdmin()}
                            data-testid="radio-aminView"
                            onChange={(e) => setSelectedUser({ ...selectedUser, roleInCompany: e.target.value })}
                            checked={selectedUser.roleInCompany === 'AdminViewOnly'}
                        />
                        <span className="margin-top-6" data-testid="lbl-radio-adminView">
                            <Text code="lblUserTypeAdminViewOnly" />
                        </span>
                    </div>
                </div>
                {showChangePassword() && (
                    <UserDetailsPassword
                        openPasswordChange={openPasswordChange}
                        setOpenPasswordChange={() => closeChangePassword()}
                        onChange={(password) => setSelectedUser({ ...user, passwordChange: { newPassword: password } })}
                    />
                )}
                <ButtonContainer>
                    <Button
                        size="sm"
                        label={text('btnSave')}
                        disabled={disableSave()}
                        loading={isLoading}
                        type="submit"
                        data-testid="btnSave"
                    />
                </ButtonContainer>
            </Form>
        </WidgetLayout>
    )
})

export default UserDetails
